// The Running Order board (CLAUDE.md: Running Order drags swap, they don't
// insert; Running Order's drag states; The swap animates on release).
//
// The rules this implements, in order of how easy they are to get wrong:
//   - Two dashed slots show the swap: the one the card came from, holding the
//     target's card dimmed as a preview, and the target's own slot, empty. The
//     card under the pointer becomes the target the moment its centre is over
//     it. Nothing else on the board moves, ever.
//   - A card becomes the target only when the dragged card's CENTRE is over it,
//     not on any slight overlap, and a locked card never does.
//   - A drop swaps exactly two cards. Releasing over a locked card, or outside
//     the board, returns the dragged card to where it came from.
//   - The swap animates both cards at once from where they really were, measured
//     before and after the order changes (FLIP). Neither jumps or fades in.
//   - A slot shows its number only while it is empty, and never over a card.
//
// Dragging is not the only way in: a card can be picked up and put down with the
// keyboard, which costs little and makes the game playable without a mouse.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { posterUrl, type RunningOrderFilm } from './types.ts';
import './Board.css';

interface Props {
  films: RunningOrderFilm[];       // by id, for lookup
  order: number[];                 // film ids, in slot order
  locked: boolean[];               // by slot
  onSwap: (a: number, b: number) => void;   // slot indexes
  disabled?: boolean;
}

interface Drag {
  fromSlot: number;
  pointerId: number;
  width: number;
  height: number;
  offsetX: number;                 // where in the card the pointer grabbed it
  offsetY: number;
  x: number;                       // current top-left of the floating card
  y: number;
  target: number | null;
}

const SWAP_MS = 220;
const RETURN_MS = 170;
const EASE = 'cubic-bezier(.4, 0, .2, 1)';

export function Board({ films, order, locked, onSwap, disabled }: Props) {
  const byId = new Map(films.map((f) => [f.id, f]));
  const boardRef = useRef<HTMLDivElement>(null);
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [drag, setDrag] = useState<Drag | null>(null);
  // The slot the card came from renders as an empty placeholder while it is being
  // dragged, so the element the pointer went down on is no longer in the page.
  // Anything listening on that element would stop hearing the drag at once, which
  // is why the move and release listeners live on the window and read the drag
  // from a ref rather than from a closure over state.
  const dragRef = useRef<Drag | null>(null);
  const setDragging = (next: Drag | null) => { dragRef.current = next; setDrag(next); };
  const [returning, setReturning] = useState<{ x: number; y: number } | null>(null);
  // The slot whose card is standing in the empty one. Mirrored in a ref because
  // the window listeners are built once per drag and would otherwise read the
  // preview as it stood in the render that made them.
  const [preview, setPreview] = useState<number | null>(null);
  const previewRef = useRef<number | null>(null);
  const showPreview = (slot: number | null) => {
    if (previewRef.current === slot) return;
    previewRef.current = slot;
    setPreview(slot);
  };
  const [held, setHeld] = useState<number | null>(null);   // keyboard: the picked-up slot

  // Where the two cards were before the order changed, so the swap can animate
  // from their real positions rather than from wherever the new layout puts them.
  // Held by slot rather than by film: the card elements are reused in place when
  // the order changes, so a map from film to element is stale exactly when it is
  // needed, and the card that was left out of the animation jumped into place
  // while the other travelled.
  const pending = useRef<{ slot: number; from: DOMRect }[] | null>(null);

  const reduced = typeof matchMedia === 'function'
    && matchMedia('(prefers-reduced-motion: reduce)').matches;

  useLayoutEffect(() => {
    const moves = pending.current;
    pending.current = null;
    if (!moves || reduced) return;
    // The dragged card sits above the one it displaces, and both above the rest,
    // from the first frame. The whole card animates, so each title travels with
    // its own poster, fully visible the whole way.
    moves.forEach(({ slot, from }, i) => {
      const el = slotRefs.current[slot]?.querySelector<HTMLElement>('.ro-card');
      if (!el) return;
      const to = el.getBoundingClientRect();
      el.style.zIndex = String(30 - i);
      const animation = el.animate(
        [{ transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)` }, { transform: 'none' }],
        { duration: SWAP_MS, easing: EASE }
      );
      animation.finished.catch(() => {}).then(() => { el.style.zIndex = ''; });
    });
  }, [order, reduced]);

  function swap(a: number, b: number, fromRect?: DOMRect, displacedAlreadyHome = false) {
    const first = slotRefs.current[a]?.getBoundingClientRect();
    const second = slotRefs.current[b]?.getBoundingClientRect();
    if (first && second) {
      pending.current = [
        { slot: b, from: fromRect ?? first },   // the dragged card, from where it was dropped
        // The displaced card travels from its real old position — unless the
        // preview has it standing in the empty slot already, in which case it is
        // where it belongs and animating it would drag it backwards first.
        ...(displacedAlreadyHome ? [] : [{ slot: a, from: second }])
      ];
    }
    onSwap(a, b);
  }

  // ---- the preview ----

  // The target's own card stands, dimmed, in the slot the held card left, so the
  // player sees where the swap would put it — and its own slot is left empty
  // behind it. It is the real card, not a copy: moving it is what clears the
  // space under the one in hand, so the two titles never sit on top of each
  // other, and two of the same poster on one board is a puzzle of its own. It is
  // there straight away and gone straight away; the slots themselves never move,
  // so the measurement is of empty boxes and can't be thrown by the transform.
  const emptySlot = drag?.fromSlot ?? null;
  useLayoutEffect(() => {
    if (preview === null || emptySlot === null) return;
    const from = slotRefs.current[preview];
    const to = slotRefs.current[emptySlot];
    const el = from?.querySelector<HTMLElement>('.ro-card');
    if (!el || !from || !to) return;
    const here = from.getBoundingClientRect();
    const there = to.getBoundingClientRect();
    el.style.transform = 'translate(' + (there.left - here.left) + 'px, ' + (there.top - here.top) + 'px)';
    // Above the cards it covers, below the one in hand. A grid item takes a
    // z-index without being positioned.
    el.style.zIndex = '40';
    return () => { el.style.transform = ''; el.style.zIndex = ''; };
  }, [preview, emptySlot]);

  // ---- pointer ----

  function onPointerDown(e: React.PointerEvent, slot: number) {
    if (disabled || locked[slot] || dragRef.current) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setHeld(null);
    showPreview(null);
    setDragging({
      fromSlot: slot, pointerId: e.pointerId,
      width: rect.width, height: rect.height,
      offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top,
      x: rect.left, y: rect.top, target: null
    });
  }

  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;

    // Which slot the card is over, by its centre: a card half over its neighbour
    // is not a drop.
    const targetAt = (current: Drag, clientX: number, clientY: number) => {
      const centreX = clientX - current.offsetX + current.width / 2;
      const centreY = clientY - current.offsetY + current.height / 2;
      let target: number | null = null;
      slotRefs.current.forEach((el, slot) => {
        if (!el || slot === current.fromSlot || locked[slot]) return;
        const r = el.getBoundingClientRect();
        if (centreX >= r.left && centreX <= r.right && centreY >= r.top && centreY <= r.bottom) target = slot;
      });
      return target;
    };

    const move = (e: PointerEvent) => {
      const current = dragRef.current;
      if (!current || e.pointerId !== current.pointerId) return;
      const target = targetAt(current, e.clientX, e.clientY);
      setDragging({
        ...current,
        x: e.clientX - current.offsetX,
        y: e.clientY - current.offsetY,
        target
      });
      showPreview(target);
    };

    const up = (e: PointerEvent) => {
      const dropped = dragRef.current;
      if (!dropped || e.pointerId !== dropped.pointerId) return;
      setDragging(null);
      // Worked out from where the release actually happened, not from the last
      // slot the card passed over: a pointer that leaves the window and comes
      // back up elsewhere would otherwise drop into a slot it is nowhere near.
      const target = targetAt(dropped, e.clientX, e.clientY);
      // The previewed card is already standing in the slot it was going to.
      const previewed = target !== null && previewRef.current === target;
      showPreview(null);
      if (target === null) {
        // Back where it came from, rather than nowhere.
        const home = slotRefs.current[dropped.fromSlot]?.getBoundingClientRect();
        if (home && !reduced) {
          setReturning({ x: dropped.x, y: dropped.y });
          requestAnimationFrame(() => setReturning({ x: home.left, y: home.top }));
          setTimeout(() => setReturning(null), RETURN_MS);
        }
        return;
      }
      swap(dropped.fromSlot, target,
        new DOMRect(e.clientX - dropped.offsetX, e.clientY - dropped.offsetY, dropped.width, dropped.height),
        previewed);
    };

    const cancel = () => { showPreview(null); setDragging(null); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', cancel);
    };
  }, [dragging, locked, order, reduced]);

  // ---- keyboard ----

  function onKeyDown(e: React.KeyboardEvent, slot: number) {
    if (disabled || locked[slot]) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (held === null) { setHeld(slot); return; }
    if (held === slot) { setHeld(null); return; }
    swap(held, slot);
    setHeld(null);
  }

  const floating = drag ? byId.get(order[drag.fromSlot]!) : null;

  return (
    <div className="ro-board" ref={boardRef}>
      {order.map((id, slot) => {
        const film = byId.get(id);
        const isOrigin = drag?.fromSlot === slot;
        const isTarget = drag?.target === slot;
        return (
          <div
            className={`ro-slot${isOrigin ? ' is-origin' : ''}${isTarget ? ' is-target' : ''}`}
            key={slot}
            ref={(el) => { slotRefs.current[slot] = el; }}
          >
            {(isOrigin || isTarget) && (
              // Two dashed boxes, and they are the pair that will swap: the slot
              // the card came from, and the target's own slot standing empty
              // behind the card that has gone to stand in it. The number belongs
              // to the empty slot and gives way to the preview.
              <span className="ro-ghost" aria-hidden="true">
                {isOrigin && preview === null && <span className="ro-slot-number">{slot + 1}</span>}
              </span>
            )}

            {film && (
              <div
                className={`ro-card${locked[slot] ? ' is-locked' : ''}${held === slot ? ' is-held' : ''}${isOrigin ? ' is-lifted' : ''}${preview === slot ? ' is-preview' : ''}`}
                role="button"
                tabIndex={locked[slot] || disabled ? -1 : 0}
                aria-label={`${film.title}, position ${slot + 1} of ${order.length}${locked[slot] ? ', correct' : ''}`}
                aria-pressed={held === slot}
                onPointerDown={(e) => onPointerDown(e, slot)}
                onKeyDown={(e) => onKeyDown(e, slot)}
              >
                <span className="ro-badgeslot">
                  {locked[slot] && <span className="ro-correct-tab">Correct</span>}
                </span>
                <Poster film={film} />
                <span className="ro-title">{film.title}</span>
              </div>
            )}
          </div>
        );
      })}

      {/* The card in the player's hand: above everything, on its own solid ground
          so nothing shows through it. */}
      {floating && drag && (
        <div className="ro-floating" style={{ transform: `translate(${drag.x}px, ${drag.y}px)`,
                                              width: drag.width, height: drag.height }}>
          <div className="ro-card is-dragging">
            <span className="ro-badgeslot" />
            <Poster film={floating} />
            <span className="ro-title">{floating.title}</span>
          </div>
        </div>
      )}
      {returning && (
        <div className="ro-floating is-returning"
             style={{ transform: `translate(${returning.x}px, ${returning.y}px)` }} aria-hidden="true" />
      )}
    </div>
  );
}

function Poster({ film }: { film: RunningOrderFilm }) {
  const url = posterUrl(film.poster);
  return url
    ? <img className="ro-poster" src={url} alt="" draggable={false} />
    : <span className="ro-poster ro-poster-missing" aria-hidden="true" />;
}
